<template>
	<div>
		<Card>
			<template #header>
				<div class="card-item">
					<span class="card-item-label">{{ $t('general.number') }}:</span>
					<span class="card-item-value"> {{ order.o2o_errand_order_sn }}</span>
				</div>
				<div class="card-item">
					<mt-badge size="small" :class="orderTypeClass">{{
						$t('menu.errand_send')
					}}</mt-badge>
				</div>
				<div class="card-item">
					<mt-badge size="small" :class="orderStateClass">{{
						$t(`order.errand_send_state.${order.o2o_errand_order_state}`)
					}}</mt-badge>
				</div>
			</template>
			<template #content>
				<div class="card-item">
					<img :src="getAddressStateAssetUrl('pickup')" class="address-icon" />
					{{
						order.o2o_errand_order_pickup_name +
							'-' +
							order.o2o_errand_order_pickup_address
					}}
				</div>
				<div class="card-item">
					<div class="hr-text"><span>{{ $t('order.order_info') }}</span></div>
				</div>
				<div class="card-item">
					<img :src="getAddressStateAssetUrl('deliver')" class="address-icon" />
					{{
						order.o2o_errand_order_deliver_name +
							'-' +
							order.o2o_errand_order_deliver_address
					}}
				</div>
				<div class="card-item">
					<div class="hr-text"></div>
				</div>
				<div class="card-item">
					<span class="card-item-label">{{ $t('order.total_amount') }}: </span>
					<span class="card-item-value">
						<span class="order-tips">
							{{ order.o2o_errand_order_amount | moneyFilter }}{{ $t('general.dollar') }}
						</span>
					</span>
				</div>
			</template>
			<template #footer>
				<div class="operators">
					<mt-button class="common-btn default badge" @click="redirectOrderDetailPage">
						{{ $t('order.order_detail') }}
						<div class="badge-red" v-if="hasUnreadMessages"></div>
					</mt-button>
					<mt-button
						class="common-btn primary"
						v-if="order.if_pay"
						@click.native="openPaymentConfirmModal({
                            choosePayment: true,
                            paymentDetail: {
                                serialNumber: order.o2o_errand_order_payment_sn,
                                paymentCode: o2o_errand_order_payment_code,
                                orderType: ErrandOrderType.SEND
                            }
                        })"
						>{{ $t('order.pay') }}</mt-button
					>
					<mt-button
						class="common-btn danger"
						v-if="order.if_cancel"
						@click.native="cancel(order.o2o_errand_order_id)"
						>{{ $t('order.cancel') }}</mt-button
					>
					<mt-button
						class="common-btn info"
						v-if="order.if_receive"
						@click.native="receive(order.o2o_errand_order_id)"
						>{{ $t('order.receive_package') }}</mt-button
					>
					<mt-button
						class="common-btn primary"
						v-if="order.if_code"
						@click.native="sendReceiveCode(order.o2o_errand_order_id)"
						>{{ $t('order.send_receive_code') }}</mt-button
					>
					<mt-button
						class="common-btn primary"
						v-if="order.if_evaluate"
						@click.native="showEvaluationModal(order.o2o_errand_order_id)"
						>{{ $t('order.comment') }}</mt-button
					>
					<mt-button
						class="common-btn danger"
						v-if="order.if_complaint"
						@click.native="complaint(order.o2o_errand_order_id)"
						>{{ $t('order_detail.complaint') }}</mt-button
					>
				</div>
			</template>
		</Card>
		<mt-popup
			v-model="showEvaluate"
			popup-transition="popup-fade"
			class="mint-popup-nobackground"
		>
			<Card :noHeader="true">
				<template slot="content">
					<div class="card-item">
						<span class="card-item-label">{{ $t('evaluation.score') }}</span>
					</div>
					<div class="card-item">
						<rating v-model="evaluation.score"></rating>
					</div>
					<div class="card-item">
						<div class="hr-text"></div>
					</div>
					<mt-field
						type="textarea"
						:placeholder="$t('evaluation.comment_input')"
						v-model="evaluation.comment"
					></mt-field>
				</template>
				<template slot="footer">
					<mt-button
						size="large"
						type="primary"
						class="common-btn ds-button-large"
						@click="submitEvaluate"
						:disabled="$v.evaluation.$invalid"
						>{{ $t('general.save') }}</mt-button
					>
				</template>
			</Card>
		</mt-popup>

	</div>
</template>

<script>
import { OrderState, ErrandOrderType } from '@/const/order'
import { fromUnixToDateTimeFilter, moneyFilter } from '@/util/filter'
import Rating from '@/components/basic/rating'
import { required, maxLength, between, integer } from 'vuelidate/src/validators'
import Card from '@/components/basic/card'
import {
	cancelErrandOrder,
	receiveErrandOrder,
	sendErrandOrderReceiveCode,
	evaluateErrandOrder
} from '@/api/memberO2oErrand'
import { Toast, MessageBox } from 'mint-ui'
import { addComplaint } from '@/api/memberO2oCompliant'
import { mapActions, mapGetters, mapState } from 'vuex'
export default {
	name: 'ErrandOrderSendCard',
	props: ['order'],
	sockets: {
		sendMessage: function (payload) {
			if (payload.roomId === this.order.room_id) {
				this.hasUnreadMessages = true
			}
		}
	},
	validations: {
		evaluation: {
			o2o_errand_order_id: {
				required,
				integer
			},
			comment: {
				maxLength: maxLength(255)
			},
			score: {
				required,
				integer,
				between: between(0, 5)
			}
		}
	},
	components: {
		Rating,
		Card
	},
	filters: {
		fromUnixToDateTimeFilter,
		moneyFilter
	},
	data () {
		return {
			ErrandOrderType,
			hasUnreadMessages: false,
			evaluation: {
				o2o_errand_order_id: 0,
				comment: '',
				score: 5
			},
			showPayment: false,
			showEvaluate: false,
			orderTypeClass: {
				label: true,
				'order-type-send': true
			},
			orderStateClass: {
				label: true,
				[`order-state-${this.order.o2o_errand_order_state}`]: true
			}
		}
	},
	computed: {
		...mapState({
			rooms: (state) => state.chatroom.rooms
		}),
		...mapGetters([
			'getUnreadCount'
		])
	},
	mounted: function () {
		const _this = this
		setTimeout(() => {
			_this.$socket.emit('getRoomIdByOrder', _this.getRoomIdParams(_this.order, 'admin'), function (res) {
				if (res.code === 0) {
					_this.order.room_id = res.results.roomId
					_this.hasUnreadMessages = _this.getUnreadCount(_this.order.room_id, 'admin') > 0
				}
			})
		}, 1500)
	},
	methods: {
		...mapActions([
			'openPaymentConfirmModal',
			'closePaymentConfirmModal'
		]),
		getRoomIdParams (val, toClientType) {
			return {
				toClientType: toClientType,
				orderId: Number(val.o2o_errand_order_id || val.order_id),
				orderType: val.errand_order_type
			}
		},
		redirectOrderDetailPage () {
			this.$router.push(
				`/member/o2o_errand_order_detail?o2o_errand_order_id=${this.order.o2o_errand_order_id}`
			)
		},
		resetEvaluationParams () {
			this.showEvaluate = false
			this.evaluation = Object.assign(
				{},
				{
					o2o_errand_order_id: 0,
					comment: '',
					score: 5
				}
			)
			this.$emit('refresh')
		},
		refresh () {
			this.$emit('refresh')
		},
		pay (paySn) {
			this.$router.push({
				name: 'MemberBuyPay',
				query: { pay_sn: paySn, pay_type: 'o2o_errand_pay_new' }
			})
		},
		cancel (orderId) {
			const _this = this
			MessageBox.confirm(this.$t('order.confirm_cancel') + '？').then((action) => {
				cancelErrandOrder(orderId)
					.then((res) => {
						_this.$emit('refresh')
					})
					.catch(function (error) {
						this.$alert.error(error.message)
					})
			})
		},
		receive (orderId) {
			const _this = this
			MessageBox.confirm(this.$t('order.receive_alert')).then((action) => {
				receiveErrandOrder(orderId)
					.then((res) => {
						_this.$emit('refresh')
					})
					.catch(function (error) {
						Toast(error.message)
					})
			})
		},
		sendReceiveCode (orderId) {
			sendErrandOrderReceiveCode(orderId)
				.then((res) => {
					Toast(res.message)
				})
				.catch(function (error) {
					Toast(error.message)
				})
		},
		showEvaluationModal (o2o_errand_order_id) {
			this.evaluation.o2o_errand_order_id = o2o_errand_order_id
			this.showEvaluate = true
		},
		submitEvaluate () {
			const _this = this
			evaluateErrandOrder(
				this.evaluation.o2o_errand_order_id,
				this.evaluation.score,
				this.evaluation.comment
			)
				.then((res) => {
					_this.resetEvaluationParams()
				})
				.catch(function (error) {
					console.error('Evaluate failed', error)
					_this.$alert.error(error.message)
				})
		},
		complaint (orderId) {
			const _this = this
			MessageBox.prompt(this.$t('order.complain_content'), '').then(({ value, action }) => {
				if (value) {
					addComplaint(orderId, 1, value)
						.then((res) => {
							this.$router.push({ name: 'MemberO2oComplaint' })
						})
						.catch(function (error) {
							console.error('Complain failed', error)
							_this.$alert.error(error.message)
						})
				}
			})
		},
		getAddressStateAssetUrl: function (addressType) {
			const status = Number(this.order.o2o_errand_order_state)
			if (
				[OrderState.PAY, OrderState.DELIVER].indexOf(status) > -1 &&
				addressType === 'pickup'
			) {
				return require('@/assets/image/svg/animation-current-circle.svg')
			} else if (status === OrderState.SEND && addressType === 'deliver') {
				return require('@/assets/image/svg/animation-current-circle.svg')
			} else {
				return require('@/assets/image/svg/inactive-circle.svg')
			}
		}
	}
}
</script>

<style lang="scss" scoped>
$cancelColor: #f47777;
$noteColor: #a6a6a6;

.label {
	padding: 0.3rem 0.5rem;
	width: auto;
	border-radius: 30px !important;
}
.order-type-send {
	background-color: $cancelColor;
}

.note {
	font-size: 0.6rem;
	color: $noteColor;
}
.order-date {
	font-size: 0.6rem;
	margin: 0.5rem 0;
}
.order-tips {
	margin-top: 1rem;
	font-size: 0.7rem;
}
</style>
